import type { LineItemStateKey } from "@/pages/(app)/returns/refund/hooks/use-line-items-state-context";
import { createContext, PropsWithChildren, useContext, useState } from "react";

/**
 * Accordion sections of the rejection dialog, per quantity state.
 * Keep in sync with the `AccordionItem` values of the articles-reject-lists
 * components.
 */
const sectionByStateKey: Partial<Record<LineItemStateKey, string>> = {
  "not-treated-by-merchant": "toHandleByMerchant",
  "validated-by-logistician": "validatedByLogistician",
  "rejected-by-logistician": "rejectedByLogistician",
  "not-treated-by-logistician": "notTreatedByLogistician",
  "not-returned": "notReturned",
};

const defaultOpenSections = ["toHandleByMerchant"];

type ReturnRejectionContextType = {
  isOpen: boolean;
  openDialog: () => void;
  closeDialog: () => void;
  openSections: string[];
  setOpenSections: (sections: string[]) => void;
  notifyScannedStateKey: (stateKey: LineItemStateKey) => void;
};

const ReturnRejectionContext = createContext<ReturnRejectionContextType>({
  isOpen: false,
  openDialog: () => {},
  closeDialog: () => {},
  openSections: defaultOpenSections,
  setOpenSections: () => {},
  notifyScannedStateKey: () => {},
});

export function useReturnRejectionContext() {
  return useContext(ReturnRejectionContext);
}

/**
 * Holds the rejection dialog state above the article scanner, so a scan can
 * add quantity to the opened rejection dialog instead of opening the
 * validation one.
 */
export function ReturnRejectionProvider({ children }: PropsWithChildren) {
  const [isOpen, setIsOpen] = useState(false);
  const [openSections, setOpenSections] =
    useState<string[]>(defaultOpenSections);

  const openDialog = () => {
    setOpenSections(defaultOpenSections);
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
  };

  /**
   * A scan can add a quantity to a collapsed section, so we reveal it to keep
   * the selected quantity (and its reason field) visible.
   */
  const notifyScannedStateKey = (stateKey: LineItemStateKey) => {
    const section = sectionByStateKey[stateKey];
    if (!section) return;
    setOpenSections((previous) =>
      previous.includes(section) ? previous : [...previous, section],
    );
  };

  return (
    <ReturnRejectionContext.Provider
      value={{
        isOpen,
        openDialog,
        closeDialog,
        openSections,
        setOpenSections,
        notifyScannedStateKey,
      }}
    >
      {children}
    </ReturnRejectionContext.Provider>
  );
}
